import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GoldDivider, CornerFlourish } from "./Ornaments";
import {
    COUPLE,
    BISMILLAH_TRANSLATION,
} from "../lib/invitationData";

const ease = [0.22, 0.61, 0.36, 1];

export const Invitation = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    const rotateY = useTransform(scrollYProgress, [0, 0.4, 1], [-72, 0, 0]);
    const opacity = useTransform(scrollYProgress, [0, 0.25, 1], [0, 1, 1]);
    const y = useTransform(scrollYProgress, [0, 0.4], [80, 0]);

    return (
        <section
            id="invitation"
            ref={ref}
            data-testid="invitation-section"
            className="relative w-full py-32 md:py-48 paper-grain overflow-hidden"
            style={{ backgroundColor: "#12100b" }}
        >
            <div
                className="mx-auto max-w-3xl px-6"
                style={{ perspective: "1600px" }}
            >
                <motion.div
                    style={{
                        rotateY,
                        opacity,
                        y,
                        transformOrigin: "left center",
                    }}
                    className="diary-page relative px-8 py-20 md:px-20 md:py-28"
                >
                    <CornerFlourish className="absolute top-6 left-6" rotate={0} />
                    <CornerFlourish className="absolute top-6 right-6" rotate={90} />
                    <CornerFlourish className="absolute bottom-6 right-6" rotate={180} />
                    <CornerFlourish className="absolute bottom-6 left-6" rotate={270} />

                    <div className="relative z-10 text-center">
                        {/* Bismillah */}
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1.4, ease }}
                            className="font-arabic text-3xl md:text-4xl text-[color:var(--gold-deep)]"
                            dir="rtl"
                            lang="ar"
                            data-testid="invitation-bismillah"
                        >
                            بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 1.2 }}
                            className="mt-3 font-serif-display italic text-sm md:text-base text-[color:var(--muted)]"
                        >
                            {BISMILLAH_TRANSLATION}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6, duration: 1 }}
                            className="mt-10"
                        >
                            <GoldDivider />
                        </motion.div>

                        {/* Host line */}
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ delay: 0.8, duration: 1.2, ease }}
                            className="mt-12 font-serif-display text-base md:text-lg leading-relaxed text-[color:var(--charcoal-soft)] max-w-xl mx-auto"
                        >
                            {COUPLE.hostLine}
                        </motion.p>

                        {/* Groom */}
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: 1.1, duration: 1.3, ease }}
                            className="relative mt-12"
                        >
                            {/* Ink-bloom flourish behind the groom name */}
                            <motion.div
                                aria-hidden="true"
                                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-0"
                                initial={{ opacity: 0, scale: 0.85 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 1.2, duration: 2.4, ease }}
                            >
                                <motion.div
                                    className="w-[420px] h-[220px] rounded-full"
                                    style={{
                                        background:
                                            "radial-gradient(ellipse at center, rgba(184,150,88,0.18) 0%, rgba(184,150,88,0.06) 40%, transparent 70%)",
                                        filter: "blur(14px)",
                                    }}
                                    animate={{ opacity: [0.7, 1, 0.7] }}
                                    transition={{
                                        duration: 8,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                />
                            </motion.div>
                            <svg
                                aria-hidden="true"
                                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-0"
                                width="440"
                                height="70"
                                viewBox="0 0 440 70"
                                fill="none"
                            >
                                <motion.path
                                    d="M20 40 C 80 12, 160 12, 220 34 C 280 56, 360 56, 420 24"
                                    stroke="#b89658"
                                    strokeWidth="0.7"
                                    strokeLinecap="round"
                                    opacity="0.55"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 2.8,
                                        delay: 1.4,
                                        ease,
                                    }}
                                />
                            </svg>

                            <p className="relative z-10 font-serif-display text-3xl md:text-5xl font-light text-[color:var(--charcoal)] tracking-wide">
                                {COUPLE.groom}
                            </p>
                            <div className="relative z-10 mt-4 space-y-1">
                                {COUPLE.groomLineage.map((line, i) => (
                                    <p
                                        key={i}
                                        className="font-serif-display italic text-sm md:text-base text-[color:var(--muted)]"
                                    >
                                        {line}
                                    </p>
                                ))}
                            </div>
                        </motion.div>

                        {/* Ampersand */}
                        <motion.p
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 1.4, duration: 1.4, ease }}
                            className="my-10 font-serif-display italic text-4xl md:text-5xl text-[color:var(--gold)]"
                        >
                            &
                        </motion.p>

                        {/* Bride */}
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: 1.5, duration: 1.3, ease }}
                            className="relative"
                        >
                            {/* Ink-bloom flourish behind the bride name */}
                            <motion.div
                                aria-hidden="true"
                                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-0"
                                initial={{ opacity: 0, scale: 0.85 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 1.6, duration: 2.4, ease }}
                            >
                                <motion.div
                                    className="w-[420px] h-[220px] rounded-full"
                                    style={{
                                        background:
                                            "radial-gradient(ellipse at center, rgba(184,150,88,0.18) 0%, rgba(184,150,88,0.06) 40%, transparent 70%)",
                                        filter: "blur(14px)",
                                    }}
                                    animate={{ opacity: [0.7, 1, 0.7] }}
                                    transition={{
                                        duration: 8,
                                        repeat: Infinity,
                                        delay: 1.5,
                                        ease: "easeInOut",
                                    }}
                                />
                            </motion.div>
                            <svg
                                aria-hidden="true"
                                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-0"
                                width="440"
                                height="70"
                                viewBox="0 0 440 70"
                                fill="none"
                                style={{ transform: "translate(-50%, -50%) rotate(180deg)" }}
                            >
                                <motion.path
                                    d="M20 40 C 80 12, 160 12, 220 34 C 280 56, 360 56, 420 24"
                                    stroke="#b89658"
                                    strokeWidth="0.7"
                                    strokeLinecap="round"
                                    opacity="0.55"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 2.8,
                                        delay: 1.8,
                                        ease,
                                    }}
                                />
                            </svg>

                            <p className="relative z-10 font-serif-display text-3xl md:text-5xl font-light text-[color:var(--charcoal)] tracking-wide">
                                {COUPLE.bride}
                            </p>
                            <div className="relative z-10 mt-4 space-y-1">
                                {COUPLE.brideLineage.map((line, i) => (
                                    <p
                                        key={i}
                                        className="font-serif-display italic text-sm md:text-base text-[color:var(--muted)]"
                                    >
                                        {line}
                                    </p>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 1.9, duration: 1 }}
                            className="mt-14"
                        >
                            <GoldDivider />
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 2.1, duration: 1 }}
                            className="mt-8 font-serif-display italic text-lg text-[color:var(--charcoal-soft)]"
                        >
                            {COUPLE.inshallah}
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 2.3, duration: 1 }}
                            className="mt-4 tracking-[0.4em] uppercase text-xs text-[color:var(--charcoal-soft)]"
                            data-testid="invitation-hero-date"
                        >
                            {COUPLE.heroDate}
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 2.5, duration: 1.2 }}
                            className="mt-14 text-[color:var(--muted)] italic font-serif-display text-sm max-w-md mx-auto leading-relaxed"
                        >
                            Your presence, prayers, and blessings would honour
                            our families on this most cherished occasion.
                        </motion.p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Invitation;
